import {
  AutocompleteInputChangeReason,
  AutocompleteRenderGroupParams,
  ListItem,
} from "@mui/material";
import { SxProps } from "@mui/system";
import { SyntheticEvent, useMemo, useState } from "react";
import { useAppSelector } from "../../../../../app/hooks";
import { RootState } from "../../../../../app/store";
import { LocalizedSkill } from "../../../../../features/talentProfile/LocalizedGroups";
import AutocompleteGroupedList from "../../AutocompleteGroupedList/AutocompleteGroupedList";
import LoadingSpinner from "../../loadingSpinner/LoadingSpinner";
import AutocompleteBase from "../autocompleteBase/AutocompleteBase";
import useTalentSkillsAutocomplete from "./useTalentSkillsAutocomplete";

interface Props {
  workTitleIds?: string[];
  value?: LocalizedSkill[] | null;
  onChange?: (skills: LocalizedSkill[] | null) => void;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  size?: "small" | "medium";
  error?: boolean;
  helperText?: string;
  sx?: SxProps;
  limitTags?: number;
  requireSearchTerm?: boolean;
}

// TODO: RHF wrapper - Create TalentSkillsAutocompleteField component that wraps this with Controller
// from react-hook-form to enable form integration with validation rules and error handling

const TalentSkillsAutocomplete = ({
  workTitleIds,
  value,
  onChange,
  multiple = true,
  disabled = false,
  fullWidth = true,
  size = "small",
  sx = {},
  limitTags,
  requireSearchTerm = false,
}: Props) => {
  const [inputValue, setInputValue] = useState<string>("");

  const localizedFieldOfWorkIds = useAppSelector(
    (state: RootState) => state.localized?.fieldOfWork,
  );

  const { skills, isLoading, setSearchTerm, resetSearch } =
    useTalentSkillsAutocomplete({
      workTitleIds,
      requireSearchTerm,
    });

  // Normalize value from IDs to LocalizedSkill objects when skills are available
  const normalizedValue = useMemo(() => {
    if (!value || value.length === 0) return multiple ? [] : null;

    // If values are already LocalizedSkill objects with proper names, return as is
    if (
      value.every(
        (v) =>
          v.name &&
          v.name !== v.id &&
          skills.some((skill) => skill.id === v.id && skill.name === v.name),
      )
    ) {
      return value;
    }

    // Normalize ID-only values to full LocalizedSkill objects
    return value
      .map((v) => {
        const found = skills.find((skill) => skill.id === v.id);
        return found || v; // Fallback to original if not found
      })
      .filter((v) => v); // Remove null/undefined
  }, [value, skills, multiple]);

  const handleChange = (newValue: LocalizedSkill | LocalizedSkill[] | null) => {
    if (onChange) {
      setInputValue("");
      resetSearch();
      if (multiple) {
        onChange(
          Array.isArray(newValue) ? newValue : newValue ? [newValue] : null,
        );
      } else {
        const singleValue = Array.isArray(newValue)
          ? newValue[0] || null
          : newValue;
        onChange(singleValue ? [singleValue] : null);
      }
    }
  };

  const getOptionLabel = (option: LocalizedSkill) => option.name;

  const isOptionEqualToValue = (
    option: LocalizedSkill,
    value: LocalizedSkill,
  ) => option.id === value.id;

  const groupBy = (option: LocalizedSkill) =>
    localizedFieldOfWorkIds[option.fieldOfWorkId] || option.fieldOfWorkId;

  const renderOption = (props: any, option: LocalizedSkill) => (
    <ListItem {...props} key={option.id}>
      {option.name}
    </ListItem>
  );

  const renderGroup = (params: AutocompleteRenderGroupParams) => (
    <AutocompleteGroupedList
      key={params.group}
      group={params.group}
      children={params.children}
    />
  );

  const handleInputChange = (
    _: SyntheticEvent,
    newValue: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "reset") {
      return;
    }

    setInputValue(newValue);

    // When the full list is already loaded, Autocomplete filters it by
    // inputValue. Server-side search is only used when a term is required.
    if (!requireSearchTerm) {
      return;
    }

    if (newValue.trim()) {
      setSearchTerm(newValue);
    } else {
      resetSearch();
    }
  };

  const onClose = () => {
    setInputValue("");
    resetSearch();
  };

  return (
    <AutocompleteBase<LocalizedSkill>
      value={normalizedValue || []}
      onChange={(newValue) => handleChange(newValue)}
      options={skills}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      limitTags={limitTags}
      size={size}
      loading={isLoading}
      loadingText={<LoadingSpinner />}
      groupBy={groupBy}
      renderGroup={renderGroup}
      renderOption={renderOption}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      onClose={onClose}
      filterOptions={requireSearchTerm ? (options) => options : undefined}
      sx={sx}
    />
  );
};

export default TalentSkillsAutocomplete;
